import { useEffect, useState } from 'react';
import {
  getAllUsers,
  updateUser,
  deleteUser,
  approveWholesale,
  declineWholesale,
  createWholesaleUser,
  getWholesaleStats,
} from '../../api/adminUsers';
import { useAuth } from '../../context/AuthContext';
import { csrf } from '../../api/auth';
import './AdminUsers.css';

const ROLE_LABELS = {
  user: 'Покупатель',
  wholesale: 'Опт',
  manager: 'Менеджер',
  admin: 'Администратор',
};

const EMPTY_WHOLESALE = {
  name: '',
  email: '',
  phone: '',
  password: '',
  companyName: '',
  inn: '',
  kpp: '',
  address: '',
  discountPercent: 0,
};

function roleLabel(role) {
  return ROLE_LABELS[role] || role || '—';
}

function confirmRoleChange(user, fromRole, toRole, isSelf) {
  if (fromRole === toRole) return true;

  const name = user.name || user.phone || user.email || 'пользователя';
  const from = roleLabel(fromRole);
  const to = roleLabel(toRole);

  let message = `Назначить роль «${to}» пользователю ${name}?\n\nСейчас: ${from} → будет: ${to}.`;

  if (toRole === 'admin' || toRole === 'manager') {
    message += '\n\nПользователь получит доступ к админ-панели.';
  }
  if (fromRole === 'admin' || fromRole === 'manager') {
    message += '\n\nStaff-роль будет снята, сессия пользователя будет сброшена.';
  }
  if (isSelf && toRole !== 'admin') {
    message += '\n\nВы снимаете с себя права администратора.';
  }

  return window.confirm(message);
}

const fmtMoney = (n) => `${Number(n || 0).toLocaleString('ru-RU')} ₽`;

export default function AdminUsers() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === 'admin';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [originalRole, setOriginalRole] = useState('user');
  const [form, setForm] = useState({});
  const [onlyPending, setOnlyPending] = useState(false);
  const [error, setError] = useState('');
  const [createForm, setCreateForm] = useState(EMPTY_WHOLESALE);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await getAllUsers();
      setUsers(data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  }

  function startEdit(u) {
    setEditing(u._id);
    setOriginalRole(u.role || 'user');
    setError('');
    setForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      isActive: !!u.isActive,
      role: u.role || 'user',
      companyName: u.wholesale?.companyName || '',
      inn: u.wholesale?.inn || '',
      kpp: u.wholesale?.kpp || '',
      address: u.wholesale?.address || '',
      discountPercent: u.wholesale?.discountPercent ?? 0,
    });
  }

  async function save() {
    const user = users.find((u) => u._id === editing);
    if (!user) return;

    if (form.role !== originalRole) {
      const isSelf = String(me?.id || '') === String(editing);
      const ok = confirmRoleChange(user, originalRole, form.role, isSelf);
      if (!ok) return;
    }

    setError('');
    try {
      await updateUser(editing, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        isActive: form.isActive,
        role: form.role,
        wholesale: {
          companyName: form.companyName,
          inn: form.inn,
          kpp: form.kpp,
          address: form.address,
          discountPercent: form.discountPercent,
        },
      });
      setEditing(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Не удалось сохранить пользователя');
    }
  }

  async function makeWholesale(u) {
    if (!window.confirm('Подтвердить оптовый статус для этого пользователя?')) return;
    await approveWholesale(u._id, {
      companyName: u.wholesale?.companyName || form.companyName || '',
      inn: u.wholesale?.inn || form.inn || '',
      kpp: u.wholesale?.kpp || form.kpp || '',
      address: u.wholesale?.address || form.address || '',
      discountPercent: u.wholesale?.discountPercent ?? form.discountPercent ?? 0,
    });
    await load();
  }

  async function rejectWholesale(u) {
    const comment = window.prompt('Комментарий к отклонению (необязательно):', '') ?? '';
    await declineWholesale(u._id, { comment });
    await load();
  }

  async function remove(id) {
    if (!window.confirm('Удалить пользователя?')) return;
    await deleteUser(id);
    await load();
  }

  async function submitCreate(e) {
    e.preventDefault();
    setCreateSaving(true);
    setError('');
    try {
      await csrf();
      await createWholesaleUser(createForm);
      setCreateForm(EMPTY_WHOLESALE);
      setCreateOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || 'Не удалось создать оптовика');
    } finally {
      setCreateSaving(false);
    }
  }

  async function openStats(u) {
    setStatsLoading(true);
    setStats(null);
    try {
      const data = await getWholesaleStats(u._id);
      setStats(data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Не удалось загрузить статистику');
    } finally {
      setStatsLoading(false);
    }
  }

  if (loading) return <p>Загрузка...</p>;

  const visible = onlyPending
    ? users.filter((u) => u.wholesale?.status === 'pending')
    : users;

  return (
    <div className="admin-users">
      <h2 className="admin-page-title">Пользователи</h2>
      {error && <p className="admin-users__error">{error}</p>}
      {!isAdmin && (
        <p className="admin-users__hint">
          Менеджер может редактировать покупателей и заявки на опт. Назначать администратора или менеджера может только admin.
        </p>
      )}

      <div className="admin-users__toolbar">
        <label style={{ display: 'inline-flex', gap: 8 }}>
          <input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} />
          Только заявки на опт
        </label>
        <button type="button" className="edit-btn" onClick={() => setCreateOpen((v) => !v)}>
          {createOpen ? 'Скрыть форму' : 'Добавить оптовика'}
        </button>
      </div>

      {createOpen && (
        <form className="admin-users__create" onSubmit={submitCreate}>
          <h3>Новый оптовик</h3>
          <div className="admin-users__create-grid">
            <label>Имя<input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} /></label>
            <label>Телефон<input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} placeholder="+7…" /></label>
            <label>Email<input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} /></label>
            <label>Пароль<input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required minLength={8} autoComplete="new-password" title="От 8 символов, буквы и цифры" /></label>
            <label>Компания<input value={createForm.companyName} onChange={(e) => setCreateForm({ ...createForm, companyName: e.target.value })} /></label>
            <label>ИНН<input value={createForm.inn} onChange={(e) => setCreateForm({ ...createForm, inn: e.target.value })} /></label>
            <label>КПП<input value={createForm.kpp} onChange={(e) => setCreateForm({ ...createForm, kpp: e.target.value })} /></label>
            <label>Адрес<input value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} /></label>
            <label>
              Скидка, %
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={createForm.discountPercent}
                onChange={(e) => setCreateForm({ ...createForm, discountPercent: e.target.value === '' ? '' : Number(e.target.value) })}
              />
            </label>
          </div>
          <p className="admin-users__hint">Скидка применяется к оптовой цене товара.</p>
          <button type="submit" className="edit-btn" disabled={createSaving}>
            {createSaving ? 'Создаю…' : 'Создать оптовика'}
          </button>
        </form>
      )}

      {(stats || statsLoading) && (
        <div className="admin-users__stats">
          <div className="admin-users__stats-head">
            <h3>
              Статистика
              {stats?.user ? `: ${stats.user.name || stats.user.phone || stats.user.email || ''}` : ''}
            </h3>
            <button type="button" className="muted" onClick={() => setStats(null)}>Закрыть</button>
          </div>
          {statsLoading && <p>Загрузка…</p>}
          {stats && !statsLoading && (
            <>
              <div className="admin-users__stats-grid">
                <div><span>Оплаченных заказов</span><b>{stats.stats.ordersCount}</b></div>
                <div><span>Сумма заказов</span><b>{fmtMoney(stats.stats.ordersRevenue)}</b></div>
                <div><span>Продаж</span><b>{stats.stats.salesCount}</b></div>
                <div><span>Выручка продаж</span><b>{fmtMoney(stats.stats.salesRevenue)}</b></div>
                <div><span>Скидка</span><b>{Number(stats.user?.wholesale?.discountPercent || 0)}%</b></div>
              </div>
              {stats.recentOrders?.length > 0 && (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>№</th>
                      <th>Статус</th>
                      <th>Сумма</th>
                      <th>Режим</th>
                      <th>Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentOrders.map((o) => (
                      <tr key={o._id}>
                        <td>{o.number}</td>
                        <td>{o.status}</td>
                        <td>{fmtMoney(o.total)}</td>
                        <td>{o.priceMode}</td>
                        <td>{o.createdAt ? new Date(o.createdAt).toLocaleString('ru-RU') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}

      <table className="tbl">
        <thead>
          <tr>
            <th>Имя</th>
            <th>Email</th>
            <th>Телефон</th>
            <th>ИНН</th>
            <th>КПП</th>
            <th>Адрес</th>
            <th>Скидка %</th>
            <th>Роль</th>
            <th>Опт</th>
            <th>Активен</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {visible.map((u) => (
            editing === u._id ? (
              <tr key={u._id}>
                <td><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></td>
                <td><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></td>
                <td><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></td>
                <td><input value={form.inn} onChange={(e) => setForm({ ...form, inn: e.target.value })} /></td>
                <td><input value={form.kpp} onChange={(e) => setForm({ ...form, kpp: e.target.value })} /></td>
                <td><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></td>
                <td>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.discountPercent}
                    onChange={(e) => setForm({ ...form, discountPercent: e.target.value === '' ? '' : Number(e.target.value) })}
                  />
                </td>
                <td>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="user">{roleLabel('user')}</option>
                    <option value="wholesale">{roleLabel('wholesale')}</option>
                    {isAdmin && <option value="manager">{roleLabel('manager')}</option>}
                    {isAdmin && <option value="admin">{roleLabel('admin')}</option>}
                  </select>
                </td>
                <td>{u.wholesale?.status || 'none'}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                </td>
                <td>
                  <button type="button" onClick={save}>Сохранить</button>
                  <button type="button" onClick={() => setEditing(null)}>Отмена</button>
                </td>
              </tr>
            ) : (
              <tr key={u._id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.phone}</td>
                <td>{u.wholesale?.inn || '—'}</td>
                <td>{u.wholesale?.kpp || '—'}</td>
                <td>{u.wholesale?.address || '—'}</td>
                <td>{u.role === 'wholesale' || u.wholesale?.status === 'approved' ? `${Number(u.wholesale?.discountPercent || 0)}%` : '—'}</td>
                <td>{roleLabel(u.role)}</td>
                <td>{u.wholesale?.status || 'none'}</td>
                <td>
                  <span className={`admin-badge ${u.isActive ? 'admin-badge--active' : 'admin-badge--inactive'}`}>
                    {u.isActive ? 'Активен' : 'Выключен'}
                  </span>
                </td>
                <td>
                  <div className="admin-users__actions">
                    <button type="button" className="edit-btn" onClick={() => startEdit(u)}>Редактировать</button>
                    {(u.role === 'wholesale' || u.wholesale?.status === 'approved') && (
                      <button type="button" className="edit-btn" onClick={() => openStats(u)}>Статистика</button>
                    )}
                    {isAdmin && (
                      <button type="button" className="muted" onClick={() => remove(u._id)}>Удалить</button>
                    )}
                    {(u.role !== 'wholesale' || u.wholesale?.status === 'pending') && (
                      <button type="button" className="edit-btn" onClick={() => makeWholesale(u)}>
                        Подтвердить опт
                      </button>
                    )}
                    {u.wholesale?.status === 'pending' && (
                      <button type="button" className="muted" onClick={() => rejectWholesale(u)}>
                        Отклонить опт
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          ))}
        </tbody>
      </table>
    </div>
  );
}
